import { PaymentElement } from '@stripe/react-stripe-js';
import { BankCard, DefaultModal, StyledText, TextButton } from 'UI';
import Button from 'UI/Button';
import httpClient from 'api/https-client';
import { PaymentsSystemsEnums } from 'constant';
import { useNotifications, useStripeSetupPaymentMethodForm } from 'hooks';
import { MonetizationLightModeProvider, StripeProvider } from 'providers';
import React, { FC, useEffect, useState } from 'react';
import { selectors, useAppSelector } from 'store';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { BaseModalProps } from 'types';

type ChangePaymentMethodModalProps = { openDeletePaymentMethodModal?: () => void } & BaseModalProps;
type PaymentMethodPermissionsResponse = { canDelete?: boolean };
type ChangePaymentMethodModalExtendedProps = ChangePaymentMethodModalProps & {
  onSuccess?: () =>
    | Promise<{ success: boolean; message?: string } | void>
    | { success: boolean; message?: string }
    | void;
  onRetryCurrentCard?: () => Promise<{ success: boolean; message?: string }>;
  showSuccessToast?: boolean;
};

const ChangePaymentMethodModal: FC<ChangePaymentMethodModalExtendedProps> = ({
  onClose,
  onSuccess,
  onRetryCurrentCard,
  showSuccessToast = true,
  openDeletePaymentMethodModal,
  ...props
}) => {
  const { successToast } = useNotifications();
  const [canDeleteCard, setCanDeleteCard] = useState(false);
  const [isRetryingCurrentCard, setIsRetryingCurrentCard] = useState(false);
  const [isRetryingAfterCardChange, setIsRetryingAfterCardChange] = useState(false);
  const [retryErrorMessage, setRetryErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { setElements, setStripe, clientSecret, handleSubmit, errorMessage, isLoading, isSuccess, isDisabled } =
    useStripeSetupPaymentMethodForm();

  const user = useAppSelector(selectors.user.selectUser);
  const paymentMethod = user?.paymentMethod;

  const onSubmitSuccess = async () => {
    try {
      setRetryErrorMessage(null);
      setIsRetryingAfterCardChange(true);

      const result = onSuccess ? await onSuccess() : undefined;

      if (result && typeof result === 'object' && 'success' in result && result.success === false) {
        setRetryErrorMessage(result.message || 'Payment failed. Please change your payment method and retry.');
        return;
      }

      setSuccessMessage(
        (result && typeof result === 'object' && 'message' in result && result.message) ||
          'Payment successful. Access restored.',
      );
      await new Promise((resolve) => setTimeout(resolve, 900));

      onClose();
      if (showSuccessToast) {
        successToast('Payment method changed successfully');
      }
    } finally {
      setIsRetryingAfterCardChange(false);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      onSubmitSuccess();
    }
  }, [isSuccess]);

  // Removal is only allowed when no subscription or installment plan still bills this card (e.g. lifetime only).
  useEffect(() => {
    if (!openDeletePaymentMethodModal || !paymentMethod) {
      return;
    }

    httpClient
      .get<PaymentMethodPermissionsResponse>('/customer/paymentMethod')
      .then(({ data }) => setCanDeleteCard(data?.canDelete === true))
      .catch(() => setCanDeleteCard(false));
  }, [openDeletePaymentMethodModal, paymentMethod?.id]);

  const onCloseModal = () => {
    if (!isLoading && !isRetryingCurrentCard && !isRetryingAfterCardChange) {
      onClose();
    }
  };

  const onRetrySavedCard = async () => {
    if (!onRetryCurrentCard || isLoading || isRetryingCurrentCard) {
      onCloseModal();
      return;
    }

    try {
      setRetryErrorMessage(null);
      setSuccessMessage(null);
      setIsRetryingCurrentCard(true);
      const result = await onRetryCurrentCard();

      if (result.success) {
        if (showSuccessToast) {
          successToast(result.message || 'Payment successful. Access restored.');
        }
        setSuccessMessage(result.message || 'Payment successful. Access restored.');
        await new Promise((resolve) => setTimeout(resolve, 900));
        onClose();
        return;
      }

      setRetryErrorMessage(result.message || 'Payment failed. Please change your payment method and retry.');
    } catch {
      setRetryErrorMessage('Payment failed. Please change your payment method and retry.');
    } finally {
      setIsRetryingCurrentCard(false);
    }
  };

  const statusMessage = isRetryingCurrentCard
    ? 'Retrying payment with current card...'
    : isRetryingAfterCardChange
    ? 'Updating card and retrying payment. Please wait...'
    : null;

  const onOpenDeletePaymentMethodModal = () => {
    onCloseModal();
    openDeletePaymentMethodModal?.();
  };

  const isBusy = isLoading || isRetryingCurrentCard || isRetryingAfterCardChange;

  return (
    <MonetizationLightModeProvider>
      <DefaultModal
        {...props}
        onRequestClose={onCloseModal}
        title="Change payment method"
        containerCSS={ContainerCSS}
        changeCloseButton
        modalCSS={ModalCSS}
        isMobileOverlayStyles>
        <Form onSubmit={handleSubmit}>
          <FormWrapper>
            <Description font="body_basic_medium_14">You can only save one card</Description>
            <BankCard
              label="Current card"
              onClick={isRetryingAfterCardChange ? undefined : onRetrySavedCard}
              card={paymentMethod?.card?.last4 || ''}
              paymentSystem={paymentMethod?.card?.brand as PaymentsSystemsEnums}
            />
            {statusMessage && <StatusText>{statusMessage}</StatusText>}
            {successMessage && <SuccessText>{successMessage}</SuccessText>}
            {retryErrorMessage && <ErrorText>{retryErrorMessage}</ErrorText>}
            <PaymentFieldsWrapper>
              <StripeProvider setStripe={setStripe} setElements={setElements} clientSecret={clientSecret}>
                <PaymentElement
                  options={{
                    terms: { card: 'never' },
                  }}
                />
              </StripeProvider>
              {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
            </PaymentFieldsWrapper>
          </FormWrapper>
          <ButtonsContainer>
            <StyledButton
              isLoading={isLoading || isRetryingAfterCardChange}
              variant="primary"
              size="middle"
              type="submit"
              disabled={isDisabled || isRetryingCurrentCard || isRetryingAfterCardChange}>
              Change card
            </StyledButton>
            {canDeleteCard && (
              <TextButton disabled={isBusy} onClick={onOpenDeletePaymentMethodModal} text="Delete card" />
            )}
          </ButtonsContainer>
        </Form>
      </DefaultModal>
    </MonetizationLightModeProvider>
  );
};

export default ChangePaymentMethodModal;

const ContainerCSS = css`
  width: 668px;
  height: fit-content;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 40px 0 60px;

  ${respondToWidth.sm`
    width: 100%;
    padding-left: 25px;
    padding-right: 25px;
  `}

  ${respondToWidth.s`
    height: 100%;
    padding: 16px 0;
    border-radius: 0;
  `}
`;

const ModalCSS = css`
  height: 100%;
  padding: 22px 0;
  overflow: hidden;

  display: flex;
  align-items: center;

  ${respondToWidth.sm`
    width: 90%;
  `}

  ${respondToWidth.s`
    padding: 0;
    width: 100%;
    height: 100%;
  `}
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  flex-grow: 1;
  gap: 40px;
  overflow-y: auto;
  padding-inline: 100px;
  overflow-x: hidden;

  ${respondToWidth.sm`
      padding-inline: 16px;
  `}
`;

const Description = styled(StyledText)`
  width: 100%;

  text-align: center;
  color: ${({ theme: { colors } }) => colors.neutrals[4]};
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const PaymentFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatusText = styled(StyledText)`
  text-align: start;

  ${({ theme: { colors, typography } }) => css`
    color: ${colors.neutrals[3]};
    ${typography.body_basic_medium_14}
  `};
`;

const SuccessText = styled(StyledText)`
  text-align: start;

  ${({ theme: { colors, typography } }) => css`
    color: ${colors.system.green};
    ${typography.body_basic_medium_14}
  `};
`;

const ErrorText = styled(StyledText)`
  text-align: start;

  ${({ theme: { colors, typography } }) => css`
    color: ${colors.system.red};
    ${typography.body_basic_medium_14}

    ${respondToWidth.s`
      ${typography.footnote_semibold_12}
  `}
  `};
`;

const StyledButton = styled(Button)`
  ${respondToWidth.s`
    margin-top: auto;
  `}
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
